import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import type { RequestParams } from '../types';
import type { InstanceUrls } from '../utils/requests';
import { useQueryFunction } from './use-query-function';

interface useRequestParams<TRequest, TResponse, TError> {
  params: RequestParams<TRequest, TResponse, TError>;
  instanceUrl?: InstanceUrls;
  config?: AxiosRequestConfig<TRequest>;
  options?: Omit<
    UseQueryOptions<AxiosResponse<TResponse>, AxiosError<TError>>,
    'queryKey' | 'queryFn'
  >;
}

export const useRequest = <TRequest, TResponse, TError>({
  params,
  instanceUrl,
  config,
  options,
}: useRequestParams<TRequest, TResponse, TError>) => {
  const { key, method, url, request, deps, fetchManual, refetchInterval } =
    params;
  const queryFn = useQueryFunction<TRequest, TResponse>(
    {
      method,
      url,
      request,
      requestConfig: config,
    },
    instanceUrl
  );

  const queryResult = useQuery<AxiosResponse<TResponse>, AxiosError<TError>>(
    [key, deps],
    queryFn,
    {
      enabled: fetchManual ? false : true,
      staleTime: 100_000_000,
      refetchInterval,
      ...options,
    }
  );

  const {
    isLoading,
    error,
    data,
    refetch,
    isFetching,
    isRefetching,
    isFetchedAfterMount,
    status,
  } = queryResult;

  return {
    isLoading,
    error,
    res: data?.data,
    refetch,
    isFetching,
    isRefetching,
    isFetchedAfterMount,
    status,
    headers: data?.headers,
  };
};
